import React, { useEffect, useState, useMemo } from "react"
import { Flex, Layout } from "antd"
import styles from "./Index.module.css"
import { Outlet, useLocation } from "react-router-dom"
import { NavigationPath } from "../ProHomeLayout/NavigationPath"
import { PageName } from "ui/PageName"
import { getSettingsMenuList } from "pagesV2/pro/Settings/ProSettings"
import { routes } from "router/routes"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import classNames from "classnames"

/**
 * Settings pages that render their own `PageName` because they put controls on the title row,
 * which this layout has no slot for. Keyed by menu-item id.
 */
const RENDERS_OWN_PAGE_NAME = new Set(["blackout"])

export const ProSettingsLayout = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { role } = useSelector((store) => store.user)
  const isStaffUser = role === "staff_user"
  const settingsMenuList = useMemo(() => getSettingsMenuList(isStaffUser), [isStaffUser])
  const [settingsItem, setSettingsItem] = useState()
  // `pageTitle` lets a menu item show a short label on its settings tile while the page
  // header and breadcrumb use a longer one; it falls back to `name` when not set.
  const pageTitle = settingsItem?.pageTitle || settingsItem?.name || t("guide.navigation.settings")
  const isLoginSecurity = settingsItem?.name === "Login & Security"
  const navigateBackPath = settingsItem ? routes.proSettingsRootPath() : routes.proHomeRootPath()

  useEffect(() => {
    // If pathname matches /pro/home/settings/template-detail, set subpage to Template Detail
    if (location.pathname.includes("/template-details")) {
      setSettingsItem({ name: "Template Details" })
      return
    }
    const selectedSettingsItem = settingsMenuList.find((menu) => menu.path === location.pathname)
    setSettingsItem(selectedSettingsItem)
  }, [location.pathname, settingsMenuList])
  // Blackout lays its Listing and Resources cards out side by side and is read next to the
  // calendar, so it uses whatever width the window gives it rather than the 1400px cap, which on
  // a wide monitor left the right-hand third of the page empty.
  const showFullWidth =
    settingsItem?.id === "profile-information" ||
    settingsItem?.id === "custom-questions" ||
    settingsItem?.id === "blackout" ||
    location.pathname.startsWith("/pro/home/settings/template-detail")
  const showWideWidth =
    settingsItem?.name === "Login & Security" ||
    settingsItem?.id === "pricing-promotions" ||
    settingsItem?.id === "taxes-and-fees" ||
    settingsItem?.id === "payment-providers"

  let contentWidthClass
  if (settingsItem) {
    if (showFullWidth) contentWidthClass = " "
    else if (showWideWidth) contentWidthClass = styles.hasWideMaxWidth
    else contentWidthClass = styles.hasMaxWidth
  }
  return (
    <Layout className={classNames(styles.layout, contentWidthClass)}>
      <Flex className={styles.header}>
        <NavigationPath subPages={settingsItem && [isLoginSecurity ? "Login & Security / Account Management" : pageTitle]} />
        {settingsItem && !isLoginSecurity && !RENDERS_OWN_PAGE_NAME.has(settingsItem.id) && !location.pathname.startsWith("/pro/home/settings/template-detail") && (
          <PageName title={pageTitle} backPath={navigateBackPath} />
        )}
      </Flex>
      <Outlet />
    </Layout>
  )
}
